package utils

/**
 * External declarations for the browser's built-in JSON object.
 * Provides safe JSON serialization to prevent injection vulnerabilities.
 */
external interface JSONAPI {
    /**
     * Converts a JavaScript value to a JSON string.
     * @param value The value to convert to a JSON string
     * @return A JSON string representation of the value
     */
    fun stringify(value: Any): String

    /**
     * Converts a JavaScript value to a JSON string with a replacer function.
     * @param value The value to convert
     * @param replacer A function that transforms the results
     * @return A JSON string
     */
    fun stringify(value: Any, replacer: ((key: String, value: Any) -> Any)?): String

    /**
     * Converts a JavaScript value to a JSON string with specific properties.
     * @param value The value to convert
     * @param replacer An array of strings that acts as a whitelist of properties
     * @return A JSON string
     */
    fun stringify(value: Any, replacer: Array<String>?): String

    /**
     * Converts a JavaScript value to a JSON string with spacing.
     * @param value The value to convert
     * @param replacer Optional replacer function or array
     * @param space A String or Number for inserting whitespace into the output JSON
     * @return A formatted JSON string
     */
    fun stringify(value: Any, replacer: dynamic, space: dynamic): String

    /**
     * Parses a JSON string and returns the corresponding JavaScript value.
     * @param text The string to parse as JSON
     * @return The corresponding JavaScript value
     */
    fun parse(text: String): dynamic

    /**
     * Parses a JSON string with a reviver function.
     * @param text The string to parse
     * @param reviver A function that can transform the results
     * @return The corresponding JavaScript value
     */
    fun parse(text: String, reviver: (key: String, value: Any) -> Any): dynamic
}

/**
 * The global JSON object provided by the browser.
 */
external val JSON: JSONAPI

/**
 * Safely serializes an object to JSON string.
 * Use this instead of string concatenation to prevent JSON injection vulnerabilities.
 *
 * @param value The object to serialize
 * @return A valid JSON string
 */
fun stringifyJson(value: Any): String {
    return JSON.stringify(value)
}
